import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Format mission time in MM:SS format
 * @param {number} minutes - Time in minutes (can include decimals)
 * @returns {string} Time formatted as MM:SS
 */
export const formatMissionTime = (minutes) => {
  const mins = Math.floor(minutes);
  const secs = Math.round((minutes - mins) * 60);
  return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
};

/**
 * Format delay time for display in data sync widgets
 * @param {number|string} value - Delay value in minutes or seconds
 * @returns {Object} Object with number and unit properties
 */
export const formatDelayTime = (value) => {
  if (!value && value !== 0) return { number: 'N/A', unit: '' };
  const numValue = parseFloat(value);
  if (isNaN(numValue)) return { number: 'N/A', unit: '' };
  
  // Convert to minutes if value seems to be in seconds (> 60)
  const minutes = numValue > 60 ? Math.round(numValue / 60) : Math.round(numValue);
  
  return {
    number: minutes.toString(),
    unit: 'mins'
  };
};

/**
 * Calculate and format sync delay between current time and last sync time
 * @param {string} lastSyncTime - ISO timestamp of last sync
 * @param {dayjs.Dayjs} [currentTime] - Current time (optional, defaults to now)
 * @returns {string} Formatted delay string (e.g., "2 days 3 hrs 15 mins")
 */
export const formatSyncDelay = (lastSyncTime, currentTime = null) => {
  if (!lastSyncTime || lastSyncTime === "N/A") {
    return "N/A";
  }

  try {
    if (!dayjs(lastSyncTime).isValid()) {
      return "N/A";
    }

    const currentUtcTime = currentTime ? dayjs(currentTime).utc() : dayjs().utc();
    const lastSyncUtcTime = dayjs.utc(lastSyncTime);

    // Calculate the time differences
    const diffInMinutes = currentUtcTime.diff(lastSyncUtcTime, "minute");
    const diffInHours = currentUtcTime.diff(lastSyncUtcTime, "hour");
    const diffInDays = currentUtcTime.diff(lastSyncUtcTime, "day");

    // Check if time difference is less than 1 minute
    if (diffInMinutes < 1) {
      return "< 1 min";
    }

    // Calculate remaining hours and minutes
    const hours = diffInHours % 24;
    const minutes = diffInMinutes % 60;

    let timeDelta = "";
    if (diffInDays > 0) {
      timeDelta += `${diffInDays} day${diffInDays > 1 ? "s" : ""} `;
    }
    if (hours > 0) {
      timeDelta += `${hours} hr${hours > 1 ? "s" : ""} `;
    }
    if (minutes > 0) {
      timeDelta += `${minutes} min${minutes > 1 ? "s" : ""}`;
    }

    return timeDelta.trim();
  } catch (error) {
    return "N/A";
  }
};

/**
 * Format sync time for display with timezone
 * @param {string} dataSyncTime - ISO timestamp to format
 * @param {string} timezone - Timezone string (e.g., "America/New_York")
 * @returns {string} Formatted time string (e.g., "3:45 PM (EST)")
 */
export const formatSyncTimeDisplay = (dataSyncTime, timezone) => {
  if (!dataSyncTime || !timezone) return "N/A";
  
  try {
    const parsedTime = dayjs.utc(dataSyncTime);
    if (!parsedTime.isValid()) return "N/A";
    
    const localTime = parsedTime.tz(timezone);
    if (!localTime.isValid()) return "N/A";
    
    return localTime.format("h:mm A (z)");
  } catch (error) {
    return "N/A";
  }
};